import { supabase, supabaseUrl, supabaseAnonKey } from './supabaseClient';

// ── Grade / board eligibility ──────────────────────────────────
const DRILL_ELIGIBLE_GRADES = [9, 10, 11, 12];
const DRILL_ELIGIBLE_BOARDS = [
  'CBSE', 'ICSE', 'State Board', 'IGCSE', 'IB MYP',
  'JEE Main', 'JEE', 'NEET', 'CBSE Board', 'ISC Board', 'A-Levels', 'IB DP',
];

export function isDrillEnabled(grade: number, board?: string): boolean {
  return DRILL_ELIGIBLE_GRADES.includes(grade) && DRILL_ELIGIBLE_BOARDS.includes(board ?? '');
}

// ── Types ──────────────────────────────────────────────────────
export interface DrillQuestion {
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option_index: number;
  explanation: string;
  skill_id: string;
}

export interface SessionResult {
  question_index: number;
  student_answer: number | null;
  correct: boolean;
  timed_out: boolean;
  skill_id: string;
}

export interface WeakConcept {
  id: number;
  name: string;
  subject_name: string;
}

export interface DrillSubject {
  id: number;
  name: string;
}

export interface DrillConcept {
  id: number;
  name: string;
}

// ── API helpers ────────────────────────────────────────────────

export async function requestDrillSet(params: Record<string, unknown>): Promise<{
  set_id: string;
  status: 'ready' | 'generating' | 'failed';
} | null> {
  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/generate-drill-questions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
      body: JSON.stringify(params),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function fetchDrillSetQuestions(setId: string): Promise<DrillQuestion[] | null> {
  const { data } = await supabase
    .from('drill_question_sets')
    .select('questions')
    .eq('id', setId)
    .eq('generation_status', 'ready')
    .maybeSingle();
  return (data?.questions as DrillQuestion[]) || null;
}

export async function pollDrillSetReady(
  setId: string,
  onTick?: () => void,
  maxWaitMs = 90000
): Promise<DrillQuestion[] | null> {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    // maybeSingle: returns null (not an error) when row is invisible due to RLS
    // (still generating) — we just keep polling until it becomes 'ready'
    const { data } = await supabase
      .from('drill_question_sets')
      .select('questions')
      .eq('id', setId)
      .eq('generation_status', 'ready')
      .maybeSingle();

    if (data?.questions) return data.questions as DrillQuestion[];
    onTick?.();
    await new Promise(r => setTimeout(r, 3000));
  }
  return null;
}

// ── Session management ─────────────────────────────────────────

export async function startDrillSession(
  userId: string,
  setId: string,
  mode: 'rapid_fire' | 'relax',
  timerMinutes: number,
  questionCount: number
): Promise<string | null> {
  const { data, error } = await supabase.rpc('start_drill_session', {
    p_user_id: userId, p_set_id: setId, p_mode: mode,
    p_timer_minutes: timerMinutes, p_question_count: questionCount,
  });
  if (error || !data) return null;
  return data as string;
}

export async function completeDrillSession(
  sessionId: string,
  userId: string,
  results: SessionResult[]
): Promise<void> {
  await supabase.rpc('complete_drill_session', {
    p_session_id: sessionId,
    p_user_id: userId,
    p_results: results,
    p_questions_attempted: results.length,
    p_questions_correct: results.filter(r => r.correct).length,
  });
}

export async function abandonDrillSession(sessionId: string, userId: string): Promise<void> {
  await supabase.rpc('abandon_drill_session', { p_session_id: sessionId, p_user_id: userId });
}

// ── Recommended: weak concepts ─────────────────────────────────

export async function getWeakConcepts(studentId: string, limit = 3): Promise<WeakConcept[]> {
  try {
    const { data: events } = await supabase
      .from('mastery_events')
      .select('concept_id, correct')
      .eq('student_id', studentId)
      .order('created_at', { ascending: false })
      .limit(300);

    if (!events?.length) return [];

    const stats: Record<number, { correct: number; total: number }> = {};
    for (const e of events as { concept_id: number; correct: boolean }[]) {
      if (!stats[e.concept_id]) stats[e.concept_id] = { correct: 0, total: 0 };
      stats[e.concept_id].total++;
      if (e.correct) stats[e.concept_id].correct++;
    }

    const weakIds = Object.entries(stats)
      .filter(([, s]) => s.total >= 3 && s.correct / s.total < 0.6)
      .sort((a, b) => (a[1].correct / a[1].total) - (b[1].correct / b[1].total))
      .slice(0, limit)
      .map(([id]) => Number(id));

    if (!weakIds.length) return [];

    const { data: concepts } = await supabase
      .from('concepts_plus1')
      .select('id, name, subjects(name)')
      .in('id', weakIds);

    return (concepts || []).map((c: any) => ({
      id: c.id,
      name: c.name,
      subject_name: c.subjects?.name ?? '',
    }));
  } catch {
    return [];
  }
}

// ── Setup data ─────────────────────────────────────────────────

export async function getDrillSubjects(): Promise<DrillSubject[]> {
  const { data } = await supabase.from('subjects').select('id, name').order('name');
  return data || [];
}

export async function getDrillConcepts(subjectId: number): Promise<DrillConcept[]> {
  const { data } = await supabase
    .from('concepts_plus1')
    .select('id, name')
    .eq('subject_id', subjectId)
    .order('name');
  return data || [];
}

// ── Archive ────────────────────────────────────────────────────

export interface DrillArchiveEntry {
  session_id: string;
  question_set_id: string;
  concept_id: number | null;
  concept_name: string | null;
  difficulty: 'easy' | 'medium' | 'hard';
  board: string;
  grade: number;
  source_type: 'ai_generated' | 'paste';
  mode: 'rapid_fire' | 'relax';
  question_count: number;
  questions_attempted: number;
  questions_correct: number;
  completed_at: string;
  results: SessionResult[];
}

export async function getDrillArchive(studentId: string): Promise<DrillArchiveEntry[]> {
  const { data } = await supabase.rpc('get_drill_archive', { p_student_id: studentId });
  return (data as DrillArchiveEntry[]) || [];
}

export async function fetchDrillSetQuestionsById(setId: string): Promise<DrillQuestion[] | null> {
  const { data } = await supabase
    .from('drill_question_sets')
    .select('questions')
    .eq('id', setId)
    .maybeSingle();
  return (data?.questions as DrillQuestion[]) || null;
}
